<script setup lang="tsx">
import type { PaginationState } from '@/types/page'
import { onBeforeUnmount, watch } from 'vue'

const props = withDefaults(
	defineProps<{
		totalPages?: number
		disableLimitSelect?: boolean
		tableTitle?: string
	}>(),
	{
		totalPages: 1,
	},
)

const PAGINATION_LIMITS = [25, 50, 100, 150]
const model = defineModel<PaginationState>({ default: { page: 1, limit: 50 } })

const storageKey = () => `pagination-${props.tableTitle}`

function saveToStorage() {
	localStorage.setItem(storageKey(), JSON.stringify(model.value))
}

function getStoredPagination(): PaginationState | null {
	const raw = localStorage.getItem(storageKey())
	return raw ? (JSON.parse(raw) as PaginationState) : null
}

onBeforeUnmount(() => {
	if (props.tableTitle) {
		const stored = getStoredPagination()

		if (stored) {
			const { page, limit: storedLimit } = stored
			if (storedLimit && PAGINATION_LIMITS.includes(storedLimit)) {
				model.value.limit = storedLimit
			}
			if (page && page >= 1) {
				model.value.page = page
			}
		} else {
			model.value.limit = PAGINATION_LIMITS[0]
			model.value.page = 1
		}
	} else {
		model.value.limit = PAGINATION_LIMITS[0]
		model.value.page = 1
	}
})

watch(model, () => {
	if (props.tableTitle) saveToStorage()
})

watch(
	() => props.totalPages,
	(newTotal) => {
		if (newTotal < 1) {
			model.value.page = 1
		} else if (model.value.page > newTotal) {
			model.value.page = newTotal
		}
	},
)

function goToPage(page: number) {
	if (page >= 1 && page <= props.totalPages && page !== model.value.page) {
		model.value.page = page
	}
}
</script>

<template>
	<div class="pagination-container">
		<button data-test="first-page-button" class="pagination-icon" @click="goToPage(1)" :disabled="model.page <= 1">
			<i class="bi bi-chevron-bar-left"></i>
		</button>

		<button data-test="prev-page-button" class="pagination-icon" @click="goToPage(model.page - 1)" :disabled="model.page <= 1">
			<i class="bi bi-chevron-left"></i>
		</button>

		<span data-test="current-page">{{ model.page }} / {{ totalPages > 0 ? totalPages : 1 }}</span>

		<button data-test="next-page-button" class="pagination-icon" @click="goToPage(model.page + 1)" :disabled="model.page >= totalPages">
			<i class="bi bi-chevron-right"></i>
		</button>

		<button data-test="last-page-button" class="pagination-icon" @click="goToPage(totalPages)" :disabled="model.page >= totalPages">
			<i class="bi bi-chevron-bar-right"></i>
		</button>

		<select
			v-if="!props.disableLimitSelect"
			v-model="model.limit"
			class="ms-3 form-select form-select-sm"
			style="width: auto"
			data-test="limit-select"
		>
			<option v-for="item in PAGINATION_LIMITS" :key="item" :value="item">{{ item }}</option>
		</select>
	</div>
</template>

<style lang="sass" scoped>
.pagination-container
  align-items: center
  margin-top: 20px
  display: flex
  justify-content: center

.pagination-icon
  background: none
  border: none
  color: #007bff
  font-size: 1.4rem
  cursor: pointer
  transition: color 0.3s ease, transform 0.2s ease

.pagination-icon:hover
  color: #0056b3
  transform: scale(1.2)

.pagination-icon:disabled
  color: #ced4da
  cursor: not-allowed
</style>
